<template>
  <div class="card">
    <header class="header-card">
      <p>
        {{ $t('produitSalon.caracteristique.title') }}
      </p>
    </header>
    <div class="card-content">
      <div class="columns is-multiline">
        <b-field :label="$t('produitSalon.caracteristique.code')" class="obligatoire column is-2">
          <b-input v-model="produit.reference"></b-input>
        </b-field>
        <b-field :label="$t('produitSalon.caracteristique.libelle')"
                 class="obligatoire column is-2">
          <b-input v-model="produit.libelle"></b-input>
        </b-field>
        <selection-agence class="column is-2 mgb-3"
                          set-first-agence-find
                          :disabled="!afficherAgence"
                          v-model="produit.agenceId"
                          :obligatoire="true">
        </selection-agence>
        <user-selector
          v-model="produit.assistantId"
          :label="$t('produitSalon.caracteristique.assistant')"
          :critere="(u) => u.profil === constantes.ASSISTANT_COMMERCIAL"
          :disabled="!!produit.regroupementProduitId"
          class="column is-2"
        />
        <b-field
          :label="$t('produitSalon.caracteristique.typeProduit')"
          :type="typeProduitFieldType"
          :message="typeProduitFieldMessage"
          class="column is-2">
          <b-autocomplete
            :value="searchTypeProduit"
            :data="typesProduitFilter"
            :loading="isFetchingTypeProduit"
            @input="getTypesProduit"
            @select="setTypeProduit"
            field="libelle"
            open-on-focus>
            <template slot="header">
              <a @click="checkPermissions(permission.maj)
              ? addEditTypeProduit() : showPopUp()"
                 :class="{'text-access-no-permission': !checkPermissions(permission.maj)}"
                 class="lien">
              <span>
                + {{$t('produitSalon.caracteristique.addNewTypeProduit')}}
              </span>
              </a>
            </template>
            <template slot="empty">
              <div v-if="!isFetchingTypeProduit">
                {{$t('general.autocomplete.noRslt')}}
              </div>
              <div v-else>
                {{$t('general.autocomplete.search')}}
              </div>
            </template>
          </b-autocomplete>
        </b-field>
        <parametre-autocomplete
          v-if="produit.produitPresse"
          :label="$t('produitPresse.modeDistribution')"
          class="column is-2"
          v-model="produit.produitPresse.modeDistributionId"
          :target="constantes.PARAMETRES_KEYS.modeDistribution"
          expanded/>
        <b-field :label="$t('produitPresse.nbTirage')" class="column is-2"
                 v-if="produit.produitPresse">
          <b-input v-model="produit.produitPresse.nbTirage" step="0.01" type="number"></b-input>
        </b-field>
        <div class="column is-2" v-if="produit.produitPresse">
          <qd-checkbox v-model="produit.controleDisponibilite" class="mgt-35px">
            {{ $t('produitPresse.controleDisponibilite') }}
          </qd-checkbox>
        </div>
        <div class="column is-2" v-if="ligneCommandeARenouvelerSpe">
          <qd-checkbox v-model="produit.renouvelable" class="mgt-35px">
            {{ $t('produitPresse.renouvelable') }}
          </qd-checkbox>
        </div>
      </div>
    </div>
    <b-modal
      v-model="popUpAddEditTypeProduit"
      class="modal"
      has-modal-card>
      <pop-up-add-edi-type-produit
        :typeProduit="newTypeProduit"
        @reloadTypeProduit="setTypeProduit"/>
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import debounce from 'lodash/debounce';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import genererTypeProduit from '@/services/presse/typeProduit/genererTypeProduit';
import selectionAgence from '@/components/general/selectionAgence';
import popUpAddEdiTypeProduit from '@/components/parametrage/typeProduit/p_addEditTypeProduit';
import userSelector from '@/components/commun/user/userSelector';
import { ASSISTANT_COMMERCIAL } from '@/configuration/parametrage/utilisateurConf';
import { TYPE_PRODUIT } from '@/configuration/presse/permissionsPresseConf';
import MixinParametreInterne from '@/mixins/parametreInterne';
import ParametreAutocomplete from '@/components/general/autocomplete/parametreAutocomplete';

const Parametres = createNamespacedHelpers('parametres');
const Societe = createNamespacedHelpers('societe');
const Permissions = createNamespacedHelpers('permissions');

export default {
  mixins: [MixinParametreInterne],
  name: 'CatalogueProduitGeneralPresseEditCaracteristique',
  props: {
    produit: {
      type: Object,
      required: false,
    },
    sousProduit: {
      type: Object,
      required: true,
    },
  },
  components: {
    selectionAgence,
    popUpAddEdiTypeProduit,
    userSelector,
    ParametreAutocomplete,
  },
  data() {
    return {
      permission: TYPE_PRODUIT,
      searchTypeProduit: '',
      typesProduitFilter: [],
      newTypeProduit: null,
      isFetchingTypeProduit: false,
      popUpAddEditTypeProduit: false,
      isTyping: false,
      constantes: {
        ASSISTANT_COMMERCIAL,
        PARAMETRES_KEYS: typesParametres.PARAMETRES_KEYS,
      },
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    ...Societe.mapGetters({
      societeActive: 'societeActive',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    afficherAgence() {
      return this.societeActive.agences.length > 1;
    },
    isTypeProduitActif() {
      if (!this.sousProduit.typeProduitId) return true;
      const typeProduit = this.parametre(typesParametres.PARAMETRES_KEYS.typeProduit)
        .find(t => t.id === this.sousProduit.typeProduitId);
      return typeProduit ? typeProduit.actif !== false : true;
    },

    typeProduitFieldType() {
      return (!this.isTypeProduitActif && !this.isTyping) ? 'is-warning' : '';
    },

    typeProduitFieldMessage() {
      return (!this.isTypeProduitActif && !this.isTyping) ? this.$t('general.messages.warning.archive') : '';
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    getTypesProduit: debounce(async function typesProduitData(text) {
      if (text === this.searchTypeProduit) return;
      this.isTyping = true;
      this.searchTypeProduit = text;

      this.isFetchingTypeProduit = true;
      try {
        await this.loadParametres([typesParametres.PARAMETRES_KEYS.typeProduit]);
        if (!text) {
          this.sousProduit.typeProduitId = null;
        }
        this.typesProduitFilter = this.parametre(typesParametres.PARAMETRES_KEYS.typeProduit)
          .filter(t => {
            if (t.actif === false) return false;

            let search = false;
            [
              t.code,
              t.libelle,
            ].forEach((item) => {
              if (item && item.toLowerCase().includes(text.toLowerCase())) {
                search = true;
              }
            });
            return search;
          });
        this.isFetchingTypeProduit = false;
      } catch (e) {
        messageErreur(this.$i18n);
        this.isFetchingTypeProduit = false;
        throw e;
      }
    }, 50),
    setTypeProduit(typeProduit) {
      if (!typeProduit || this.sousProduit.typeProduitId === typeProduit.id) return;
      this.searchTypeProduit = typeProduit.libelle;
      this.sousProduit.typeProduitId = typeProduit.id;
      this.popUpAddEditTypeProduit = false;
      this.typesProduitFilter = [typeProduit];
      this.isTyping = false;
    },
    addEditTypeProduit() {
      this.newTypeProduit = genererTypeProduit();
      this.popUpAddEditTypeProduit = true;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([typesParametres.PARAMETRES_KEYS.typeProduit]);
      const allTypesProduit = this.parametre(typesParametres.PARAMETRES_KEYS.typeProduit);
      this.typesProduitFilter = allTypesProduit.filter(t => t.actif !== false);
      if (this.sousProduit.typeProduitId) {
        const typeProduit = allTypesProduit.find(t => t.id === this.sousProduit.typeProduitId);
        if (typeProduit) {
          this.searchTypeProduit = typeProduit.libelle;
          this.typesProduitFilter = [typeProduit];
        }
      }
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>

<style scoped>

</style>
